import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, Wind, Star, MessageCircle, BookOpen, HelpCircle, Users, ArrowRight } from "lucide-react";

const MOODS_MAP = {
  happy: { label: "Happy", color: "#E5B95F" },
  calm: { label: "Calm", color: "#4A6B53" },
  grateful: { label: "Grateful", color: "#D2A679" },
  anxious: { label: "Anxious", color: "#C86B55" },
  sad: { label: "Sad", color: "#8A918C" },
  angry: { label: "Angry", color: "#9A4D3B" },
  tired: { label: "Tired", color: "#585D59" },
  hopeful: { label: "Hopeful", color: "#B5C7A3" },
  overwhelmed: { label: "Overwhelmed", color: "#C86B55" },
  confused: { label: "Confused", color: "#E5B95F" },
};

const features = [
  { to: "/check-in", icon: Heart, title: "Daily Check-in", desc: "How are you really feeling today? Your mood wheel, gentle prompts, and a streak that celebrates you.", color: "bg-terracotta-light text-terracotta" },
  { to: "/calm", icon: Wind, title: "Calm Tools", desc: "Breathing exercises, body scans, and grounding techniques for when the world feels too loud.", color: "bg-sage-light text-sage" },
  { to: "/affirmations", icon: Star, title: "Affirmations", desc: "Words of strength, truth, and heart. A daily card just for you, plus a wins board to celebrate.", color: "bg-[#FFF8E7] text-warmyellow" },
  { to: "/talk", icon: MessageCircle, title: "Talk to Sovha", desc: "Your safe space to vent, think out loud, or ask for a plan. Like talking to a big sister who gets it.", color: "bg-terracotta-light text-terracotta" },
  { to: "/journal", icon: BookOpen, title: "My Journal", desc: "A private diary with mood tags, guided prompts, and a safe place for your thoughts.", color: "bg-sage-light text-sage" },
  { to: "/understand", icon: HelpCircle, title: "Understand What's Happening", desc: "Learn about coercive control, financial abuse, love bombing, and more. Knowledge is power.", color: "bg-[#FFF8E7] text-warmyellow" },
  { to: "/support", icon: Users, title: "For Someone I Love", desc: "Supporting someone? Learn how to help without pushing, what to say, and how to take care of yourself too.", color: "bg-terracotta-light text-terracotta" },
];

export default function HomePage() {
  const [continuity, setContinuity] = useState(null);

  useEffect(() => {
    const fetchContinuity = async () => {
      try {
        const res = await api.get(`/continuity`);
        if (res.data.has_history) setContinuity(res.data);
      } catch (e) { console.error(e); }
    };
    fetchContinuity();
  }, []);

  const moodInfo = continuity ? MOODS_MAP[continuity.last_mood] : null;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-terracotta-light/40 to-earth-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-20 sm:py-32 text-center">
          {/* Continuity Greeting */}
          {continuity && moodInfo && (
            <div
              className="inline-flex items-center gap-3 mb-8 px-5 py-3 rounded-full border animate-fade-in-up"
              style={{ backgroundColor: moodInfo.color + "10", borderColor: moodInfo.color + "30" }}
              data-testid="continuity-greeting"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: moodInfo.color + "25", color: moodInfo.color }}>
                {moodInfo.label[0]}
              </div>
              <p className="text-earth-700 text-sm">
                {continuity.days_ago === 0
                  ? <>You checked in today feeling <strong style={{ color: moodInfo.color }}>{moodInfo.label.toLowerCase()}</strong>. Welcome back.</>
                  : <>Last time you felt <strong style={{ color: moodInfo.color }}>{moodInfo.label.toLowerCase()}</strong>, {continuity.days_ago} day{continuity.days_ago !== 1 ? "s" : ""} ago. How are you now?</>
                }
              </p>
            </div>
          )}

          {!continuity && (
            <div className="inline-block mb-6">
              <img src="/sovha-logo.png" alt="Sovha" className="h-[8.5rem]" />
            </div>
          )}

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-earth-800 mb-6 leading-tight tracking-tight">
            Your big sister,<br />
            <span className="font-normal text-terracotta">always in your corner</span>
          </h1>
          <p className="text-earth-600 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            A warm, safe space to check in with yourself, find calm, journal your thoughts, 
            and feel a little less alone. No judgments. No pressure. Just you, supported.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/check-in" data-testid="hero-checkin-btn" className="bg-terracotta text-earth-50 rounded-full px-8 py-4 font-medium hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-center gap-2">
              Start your check-in <ArrowRight size={18} />
            </Link>
            <Link to="/talk" data-testid="hero-talk-btn" className="border border-earth-300 text-earth-700 rounded-full px-8 py-4 font-medium hover:bg-earth-100 transition-all duration-300">
              Talk to Sovha
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="features-section">
        <div className="text-center mb-16">
          <p className="text-earth-500 text-sm uppercase tracking-[0.15em] font-bold mb-3">Everything you need</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-earth-800 tracking-tight">A space built around you</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Link key={f.to} to={f.to} data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 hover:shadow-[0_12px_40px_rgba(45,49,46,0.08)] hover:-translate-y-1 transition-all duration-500">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${f.color} mb-5`}><Icon size={22} /></div>
                <h3 className="font-heading text-xl text-earth-800 mb-3">{f.title}</h3>
                <p className="text-earth-600 text-sm leading-relaxed mb-4">{f.desc}</p>
                <span className="inline-flex items-center gap-1 text-terracotta text-sm font-medium group-hover:gap-2 transition-all duration-300">Explore <ArrowRight size={14} /></span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Gentle CTA */}
      <section className="bg-sage-light/50 py-16 sm:py-24 px-4" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-earth-800 mb-6 tracking-tight">You don't have to do this alone</h2>
          <p className="text-earth-600 text-base sm:text-lg mb-8 leading-relaxed">
            Whether you need to vent, ground yourself, or just know someone cares - Sovha is here. Think of it like a big sister who always picks up.
          </p>
          <Link to="/talk" data-testid="cta-talk-btn" className="inline-flex items-center gap-2 bg-sage text-earth-50 rounded-full px-8 py-4 font-medium hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
            <MessageCircle size={18} /> Talk to Sovha
          </Link>
        </div>
      </section>
    </div>
  );
}
